/**
 * Luxe · Material3DResolver (Phase 2B-1).
 *
 * Resolves the exact Material ID for a physical surface using the
 * canonical closest-explicit-wins hierarchy:
 *   Cabinet Default (material_slots.<slot> or item.material_id)
 *     → Bay Override (bay.material_id / bay.back_material_id / bay.shelf_material_id / bay.interior_material_id)
 *       → Individual Surface Override (drawer.material_id / shelf.material_id / front_override.material_id)
 *
 * Also resolves the pinned Visual Asset version from a Design Option's
 * scene_versions.visual_asset_pins so Option A never silently upgrades
 * to a newer Master material asset when V2 is created.
 */

/** Resolved material record: { material_id, source, asset_version|null,
 *  asset_id|null, mapping|null }. */
export function resolveSurface({
  item,
  scope,           // "front" | "carcass" | "top" | "back" | "shelf_default" | "interior"
  bayIndex = null,
  drawerId = null,
  shelfId  = null,
  frontIndex = null,
  pinsByMaterialId = {},   // { material_id: {asset_id, asset_version, mapping} }
}) {
  const bay = bayIndex != null ? (item.cabinet_config?.bays || [])[bayIndex] : null;
  // 1. Individual surface override
  if (bay && drawerId != null) {
    const d = (bay.drawers || []).find((x) => x && x.id === drawerId);
    if (d && d.material_id) return _wrap(d.material_id, "drawer", pinsByMaterialId);
  }
  if (bay && shelfId != null) {
    const s = (bay.shelves_config || []).find((x) => x && x.id === shelfId);
    if (s && s.material_id) return _wrap(s.material_id, "shelf", pinsByMaterialId);
  }
  if (bay && frontIndex != null) {
    const f = (bay.front_overrides || [])[frontIndex];
    if (f && f.material_id) return _wrap(f.material_id, "front_override", pinsByMaterialId);
  }
  // 2. Bay-level override for the scope
  if (bay) {
    const bayKey = _bayKeyForScope(scope);
    if (bayKey && bay[bayKey]) return _wrap(bay[bayKey], "bay", pinsByMaterialId);
  }
  // 3. Cabinet default via material_slots or primary material_id
  const slotMap = item.material_slots || {};
  const slotKey = _slotKeyForScope(scope);
  if (slotKey && slotMap[slotKey]?.material_id) {
    const mid = slotMap[slotKey].material_id;
    const w = _wrap(mid, "slot", pinsByMaterialId);
    // Slot-level mapping override (Phase 2A-ii Stage C) — takes
    // precedence over Master material default mapping.
    if (slotMap[slotKey].mapping) w.mapping = slotMap[slotKey].mapping;
    return w;
  }
  if (item.material_id) return _wrap(item.material_id, "primary", pinsByMaterialId);
  return null;
}

function _bayKeyForScope(scope) {
  if (scope === "front")         return "material_id";
  if (scope === "back")          return "back_material_id";
  if (scope === "shelf_default") return "shelf_material_id";
  if (scope === "interior")      return "interior_material_id";
  return null;
}
function _slotKeyForScope(scope) {
  if (scope === "front")   return "front";
  if (scope === "carcass") return "carcass";
  if (scope === "top")     return "top";
  return null;
}
function _wrap(mid, source, pins) {
  const pin = pins[mid] || null;
  return {
    material_id: mid,
    source,
    asset_id:      pin?.asset_id      || null,
    asset_version: pin?.asset_version || null,
    readiness_level: pin?.readiness_level || null,
    mapping: pin?.mapping || null,
  };
}
